import React, { lazy, Suspense } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { ThemeProvider } from "./components/ThemeProvider";
import { CurrencyProvider } from "./components/CurrencyProvider";
import { LanguageProvider } from "./i18n/LanguageContext";
import ErrorBoundary from "./components/ErrorBoundary";
import OrionLanding from "./components/OrionLanding";
import OrionHeader from "./components/OrionHeader";
import FloatingFeedbackButton from "./components/FloatingFeedbackButton";
import SeoManager from "./components/Seo";

// Everything except the home page is split into its own chunk and loaded on demand.
const CompanyDetail = lazy(() => import("./components/CompanyDetail"));
const ContactPage = lazy(() => import("./components/ContactPage"));
const FeedbackPage = lazy(() => import("./components/FeedbackPage"));
const DashboardPage = lazy(() => import("./components/DashboardPage"));
const LegalPage = lazy(() => import("./components/LegalPage"));
const NotFound = lazy(() => import("./components/NotFound"));

// Growth and Acceleration now live as sections on the one-page landing.
const toSection = (id) => <Navigate to={{ pathname: "/", hash: `#${id}` }} replace />;

// Team lives on the Draper Dragon site.
const ExternalRedirect = ({ to }) => {
  window.location.replace(to);
  return null;
};

const PageFallback = () => <div className="of-page min-h-screen" aria-busy="true" />;

function App() {
  return (
    <div className="App">
      <ErrorBoundary>
        <LanguageProvider>
        <ThemeProvider>
          <CurrencyProvider>
              <BrowserRouter>
                <SeoManager />
                <Suspense fallback={<PageFallback />}>
                <Routes>
                  {/* Orion Fund Landing Page */}
                  <Route path="/" element={
                    <>
                      <OrionHeader />
                      <OrionLanding />
                      <FloatingFeedbackButton />
                    </>
                  } />
                  
                  {/* Contact Page */}
                  <Route path="/contact" element={<ContactPage />} />
                  <Route path="/apply" element={<ContactPage />} />
                  
                  {/* Feedback Page */}
                  <Route path="/feedback" element={<FeedbackPage />} />
                  
                  {/* Old standalone pages -> sections of the one-page landing */}
                  <Route path="/growth" element={toSection("growth")} />
                  <Route path="/cometa-labs" element={toSection("growth")} />
                  <Route path="/acceleration" element={toSection("acceleration")} />
                  <Route path="/team" element={<ExternalRedirect to="https://www.draperdragon.com/#team" />} />
                  {/* Media page removed */}
                  <Route path="/media" element={<Navigate to="/" replace />} />
                  <Route path="/media/:slug" element={<Navigate to="/" replace />} />

                  {/* Legal */}
                  <Route path="/terms" element={
                    <>
                      <OrionHeader />
                      <LegalPage doc="terms" />
                    </>
                  } />
                  <Route path="/privacy" element={
                    <>
                      <OrionHeader />
                      <LegalPage doc="privacy" />
                    </>
                  } />

                  {/* Dashboard Routes */}
                  <Route path="/dashboard" element={
                    <>
                      <OrionHeader />
                      <DashboardPage />
                    </>
                  } />
                  <Route path="/company/:companyName" element={
                    <>
                      <OrionHeader />
                      <CompanyDetail />
                    </>
                  } />

                  {/* 404 */}
                  <Route path="*" element={
                    <>
                      <OrionHeader />
                      <NotFound />
                    </>
                  } />
                </Routes>
                </Suspense>
              </BrowserRouter>
          </CurrencyProvider>
        </ThemeProvider>
        </LanguageProvider>
      </ErrorBoundary>
    </div>
  );
}

export default App;
